import { format, utcToZonedTime } from "date-fns-tz";
import { getDomainConfig } from "../config/config";
/** Create the full ticket title based on cart item passed in. */
export const getTicketTitle = (cartItem, includeType = true) => {
  let cartItemTitle = '';
  const config = getDomainConfig();

  // If it's a hotel item, add the attraction title.
  if (cartItem.tags.indexOf('rooms') !== -1) {
    cartItemTitle += cartItem.data.attraction_title + ' ';
  }

  // We always include the basic title.
  cartItemTitle += cartItem.title;

  if (cartItem.internal.price_option_title !== ""){
    cartItemTitle += ` (${cartItem.internal.price_option_title})`;
  }

  if (cartItem.internal.type !== "" && includeType) {
    cartItemTitle += " (" + cartItem.internal.type + ")";
  }

  if (cartItem.internal.type_description !== "" && includeType) {
    cartItemTitle += " - " + cartItem.internal.type_description;
  }

  if (cartItem.year !== "") {
    cartItemTitle += " - " + cartItem.year;
  }

  if (cartItem.date !== "") {
    cartItem.tags.some((tag) => tag.includes('rooms')) && config.brand === 'Atd' ?
      cartItemTitle += " - " + format(utcToZonedTime(new Date(cartItem.attributes[5][0]),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' }) + " - " + format(utcToZonedTime(new Date(cartItem.attributes[42][0]),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' }) :
      cartItemTitle += " - " + format(utcToZonedTime(new Date(cartItem.date),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' });
  }

  if (cartItem.time !== "") {
    cartItemTitle += " - " + cartItem.time;
  }

  // If this is a combo we now include the subtickets as they could have different dates.
  if (cartItem.data && cartItem.data.kit_id && cartItem.data.kit_id !== '') {
    // Build an array of attributes.
    let componentAttributes = [];
    // Set title.
    const attributes = cartItem.internal.attributes;
    attributes.forEach((attribute) => {
      const dateStr = attribute.values[0];
      const aid = parseInt(attribute.attribute_id.split('-')[2]);
      const ticketId = parseInt(attribute.attribute_id.split('-')[0]);
      const dateAid = 5; // The attribute id for date.
      const timeAid = 10; // The attribute id for time.
      if (!componentAttributes[ticketId]) {
        if (attribute.product_title && attribute.product_title !== "") {
          componentAttributes[ticketId] = [];
          componentAttributes[ticketId].title = attribute.product_title;
        }
        else {
          // We don't have the new format, skip this for now.
          return;
        }
      }

      if (aid === dateAid) {
        const dateAttr = format(utcToZonedTime(new Date(dateStr),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' });
        componentAttributes[ticketId].date = dateAttr;
      }
      else if (aid === timeAid) {
        const timeAttr = attribute.values[0];
        componentAttributes[ticketId].time = timeAttr;
      }
    });

    // Now we have all component attributes we can display them correctly.
    componentAttributes.forEach((componentAttribute) => {
      cartItemTitle += `<div>${componentAttribute.title}`;

      // Add date/time as needed.
      if (componentAttribute.date && componentAttribute.date !== "" ) {
        cartItemTitle += ` - ${componentAttribute.date}`;
      }
      if (componentAttribute.time && componentAttribute.time !== "" ) {
        cartItemTitle += ` - ${componentAttribute.time}`;
      }

      cartItemTitle += `</div>`;

    });
  }

  return cartItemTitle;

};

/**
 * Convert given date to another format.
 * @param date     Can be a UK date (dd/mm/yy or dd/mm/yyyy), ISO (yyyy-mm-dd) or js date object.
 * @param output   Required output format - "uk", "uk_short", "iso", "utc" or "obj" (default).
 *
 */
export const getDate = (date, output = 'obj') => {
  // Convert the input date into a javascript date if needed:
  let dateObj;
  const hour = 12;
  let tmp;
  if (typeof (date) === 'object') {
    dateObj = date;
  } else if (date.match(/^[0-9]{2}\/[0-9]{2}\/[0-9]{2}$/)) { // dd/mm/yy
    tmp = date.split('/');
    if (tmp[2].length === 2) tmp[2] = (tmp[2] < 30) ? '20' : '19';
    dateObj = new Date(tmp[2], Number(tmp[1]) - 1, tmp[0], hour);
  } else if (date.match(/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/)) { // dd/mm/yyyy
    tmp = date.split('/');
    dateObj = new Date(tmp[2], Number(tmp[1]) - 1, tmp[0], hour);
  } else if (date.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) { // yyyy-mm-dd
    tmp = date.split('-');
    dateObj = new Date(tmp[0], Number(tmp[1]) - 1, tmp[2], hour);
  } else {
    // eslint-disable-next-line no-console
    return console.warn('getDate() - Invalid input date supplied.', date);
  }

  // Break down the date into string parts:
  const d = dateObj.getDate().toString().padStart(2, '0');
  const m = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const y = dateObj.getFullYear().toString();
  const ys = y.substr(2);

  // Return date string in the required format:
  switch (output) {
    case 'utc': return Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    case 'uk': return `${d}/${m}/${y}`;
    case 'uk_short': return `${d}/${m}/${ys}`;
    case 'iso': return `${y}-${m}-${d}`;
    default: break;
  }

  // Return date object if requested:
  return dateObj;
};

/**
 * Function that cycles cart contents and gets earliest dated product (if exists).
 *
 * @param cartContents
 * @returns {string}
 */
export const getCartEarliestDate = (cartTickets, cartMeta) => {

  let earliestDate = '';
  if (typeof cartMeta.earliest_ticket_date !== undefined) {
    // From Apr (est.) we set this as a value in the cart meta.
    if (cartMeta.earliest_ticket_date) {
      earliestDate = cartMeta.earliest_ticket_date;
    }
  }

  return earliestDate;
};

/**
 * Helper function to get list of guests for a hotel cart.
 *
 * @param cartTickets
 */
export const getGuestListDetails = (ticketAttributes) => {

  let guestListDetails = {};


  Object.keys(ticketAttributes).forEach((att) => {

  // eslint-disable-next-line default-case
    switch (att) {
      case '48':
        guestListDetails['adult'] = ticketAttributes[att][0];
        break;

      case '49':
        guestListDetails['child'] = ticketAttributes[att][0];
        break;

      case '87':
        guestListDetails['infant'] = ticketAttributes[att][0];
        break;
    }
  });
  return guestListDetails;
};

/**
 *  Some tickets adds an extra null value to the attributes.values array.
 *  Therefore some names are being lost in the cart.
 *
 *  Hence this function that will filter through the request body
 *  and removes any null values before makes the request to the cart/attributes endpoint.
 */

export const filterNullValues = (obj) => {

  Object.keys(obj).forEach((key) => {

    if (Array.isArray(obj[key])) {

      obj[key] = obj[key].filter(value => value !== null && value !== undefined);
      obj[key].forEach((item) => {

        if (typeof item === 'object') {
          filterNullValues(item);
        }
      });

    } else if (typeof obj[key] === 'object') {
      filterNullValues(obj[key]);
    }
  });
  return obj;
}
